// The panel a feature page renders when the gate says it is off for this practice.
//
// Reachable by deep link (a bookmarked /p/3/worklist) even though the sidebar hides
// the entry, and by anyone who forced a feature off in Practice Details. It says
// which feature, why it is off, and where to turn it on — an empty page that looks
// broken is what this replaces.

import React from 'react';
import './FeatureOff.css';

// Human wording for the feature keys. Kept here rather than in the registry so a
// backend-only feature (no sidebar entry of its own) can still explain itself.
const LABELS = {
  roster: 'Upcoming roster',
  eligibility: 'Eligibility',
  copay: 'Copay estimates',
  patients: 'Patients',
  authorizations: 'Authorizations',
  charge_capture: 'Charge capture',
  rules: 'Rules',
  statements: 'Patient statements',
  worklist: 'Denial worklist',
  writeoffs: 'Write-offs',
  claim_editor: 'Claim editor',
  fee_schedule: 'Fee schedule',
  submit_queue: 'Submit queue',
};

// What a feature needs, in the language a biller uses rather than capability keys.
const NEEDS = {
  roster: 'a schedule feed from this practice’s EMR or PM system',
  eligibility: 'patient coverage data (payer and member ID)',
  copay: 'patient coverage data (payer and member ID)',
  charge_capture: 'a schedule feed from this practice’s EMR or PM system',
  statements: 'remittance data (835s) so balances are real',
  worklist: 'claim status data (277CA or a status column in the export)',
};

// Features that are off because the practice HAS a capability, not because it lacks
// one (the backend's `hidden_when`). "Nothing connected supplies it" would be wrong
// for these, so their sentence wins over NEEDS and the generic text.
// Submit-queue sprint, Instance B.
const HIDDEN_BECAUSE = {
  submit_queue: 'This practice submits through Stedi; the queue is for practices Atlas cannot submit for.',
};

export default function FeatureOff({ feature, practiceName, isAdmin, onOpenPracticeDetails }) {
  const label = LABELS[feature] || feature;
  const needs = NEEDS[feature];
  const hiddenBecause = HIDDEN_BECAUSE[feature];

  return (
    <div className="feature-off">
      <div className="feature-off-card">
        <h2>{label} is off{practiceName ? ` for ${practiceName}` : ''}</h2>
        <p>
          {hiddenBecause
            ? hiddenBecause
            : needs
            ? `Atlas turns this on when the practice has ${needs}. Nothing connected to this practice supplies it yet.`
            : 'This feature has been turned off for this practice.'}
        </p>
        {isAdmin && (
          <p className="feature-off-hint">
            An admin can force it on under <strong>Practice Details &rarr; Integrations &amp; features</strong>.
            {' '}A forced-on feature may have little to show until a data source is connected.
          </p>
        )}
        {isAdmin && onOpenPracticeDetails && (
          <button className="btn btn-secondary" onClick={onOpenPracticeDetails}>
            Open Practice Details
          </button>
        )}
      </div>
    </div>
  );
}
